import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Globe, Loader2, MapPin, Clock, CheckCircle2, XCircle, Server } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface LatencyResult {
  url: string;
  latency: number | null;
  statusCode: number;
  success: boolean;
  serverRegion: string;
  error?: string;
  timestamp: string;
}

export const GlobalLatencyTester = () => {
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<LatencyResult[]>([]);

  const testLatency = async () => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setIsLoading(true);
    setResults([]);

    try {
      // Run multiple tests from the edge function
      const testPromises = Array(5).fill(null).map(async (_, index) => {
        // Add small delay between requests to get varied results
        await new Promise(resolve => setTimeout(resolve, index * 200));
        
        const { data, error } = await supabase.functions.invoke("latency-test", {
          body: { url: url.trim() },
        });

        if (error) {
          console.error("Latency test error:", error);
          return null;
        }

        return data as LatencyResult;
      });

      const responses = await Promise.all(testPromises);
      const validResults = responses.filter((r): r is LatencyResult => r !== null);
      
      setResults(validResults);
      
      if (validResults.length > 0 && validResults.some(r => r.success)) {
        toast.success("Latency test complete!");
      } else if (validResults.length > 0) {
        toast.error("Failed to reach the URL", {
          description: validResults[0]?.error || "Unknown error",
        });
      }
    } catch (error) {
      console.error("Error running latency test:", error);
      toast.error("Failed to run latency test");
    } finally {
      setIsLoading(false);
    }
  };

  const getLatencyColor = (latency: number | null): string => {
    if (latency === null) return "text-muted-foreground";
    if (latency < 200) return "text-green-500";
    if (latency < 500) return "text-yellow-500";
    return "text-red-500";
  };

  const getLatencyBadge = (latency: number | null) => {
    if (latency === null) return null;
    if (latency < 200) return <Badge className="bg-green-500/20 text-green-600 border-green-500/30">Fast</Badge>;
    if (latency < 500) return <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">Average</Badge>;
    return <Badge className="bg-red-500/20 text-red-600 border-red-500/30">Slow</Badge>;
  };

  const successfulResults = results.filter(r => r.success && r.latency !== null);
  const avgLatency = successfulResults.length > 0
    ? Math.round(successfulResults.reduce((sum, r) => sum + (r.latency || 0), 0) / successfulResults.length)
    : null;
  const minLatency = successfulResults.length > 0
    ? Math.min(...successfulResults.map(r => r.latency!))
    : null;
  const maxLatency = successfulResults.length > 0
    ? Math.max(...successfulResults.map(r => r.latency!))
    : null;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground">
          Test your website's response time from our edge servers. Multiple requests measure consistency.
        </p>
      </div>

      {/* URL Input */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter website URL (e.g., example.com)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && testLatency()}
              />
            </div>
            <Button 
              onClick={testLatency} 
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4" />
                  Test Latency
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {successfulResults.length > 0 && avgLatency !== null && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Average</p>
              <p className={`text-2xl font-bold ${getLatencyColor(avgLatency)}`}>
                {avgLatency}ms
              </p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/5 border-green-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Fastest</p>
              <p className="text-2xl font-bold text-green-500">
                {minLatency}ms
              </p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/5 border-red-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Slowest</p>
              <p className="text-2xl font-bold text-red-500">
                {maxLatency}ms
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Assessment */}
      {avgLatency !== null && (
        <Card className={avgLatency < 300 ? "bg-green-500/10 border-green-500/30" : avgLatency < 600 ? "bg-yellow-500/10 border-yellow-500/30" : "bg-red-500/10 border-red-500/30"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Server className={`w-5 h-5 ${avgLatency < 300 ? "text-green-500" : avgLatency < 600 ? "text-yellow-500" : "text-red-500"}`} />
              <div>
                <p className="font-medium">
                  {avgLatency < 300 ? "Excellent Performance" : avgLatency < 600 ? "Good Performance" : "Needs Improvement"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {avgLatency < 300 
                    ? "Your site responds quickly from our edge servers." 
                    : avgLatency < 600 
                    ? "Response time is acceptable but could be optimized."
                    : "Consider using a CDN or optimizing server response."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results List */}
      {results.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            Test Results ({results.length} requests)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {results.map((result, index) => (
              <Card 
                key={index}
                className={!result.success ? "opacity-60" : ""}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <Server className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Request #{index + 1}</p>
                      <p className="text-xs text-muted-foreground">
                        {result.serverRegion !== "unknown" ? result.serverRegion : "Edge Server"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <>
                        <div className="text-right">
                          <p className={`font-bold ${getLatencyColor(result.latency)}`}>
                            {result.latency}ms
                          </p>
                        </div>
                        {getLatencyBadge(result.latency)}
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-muted-foreground">
                          {result.error || "Failed"}
                        </span>
                        <XCircle className="w-4 h-4 text-red-500" />
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {results.length === 0 && !isLoading && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Enter a URL above to test response times from our edge servers
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card className="bg-muted/50">
        <CardContent className="p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-2">How it works:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>We send HTTP HEAD requests from our edge server to your URL</li>
            <li>Multiple requests help measure consistency and identify variance</li>
            <li>Results show the time for the server to respond (not full page load)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
